import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.string.trim.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { useFetchContentUsersAndTeams, useShareContent } from 'actions/content';
import useFocusOnMount from 'hooks/useFocusOnMount';
import Panel from 'components/Panel';
import FormStack from 'components/FormStack';
import FormField, { DefaultFormFieldLayout } from 'components/FormField';
import MultiSelect, { getOptionDivider, getOptionGroupLabel } from 'components/MultiSelect';
import getShareRefIds from '../shared/getShareRefIds';
import { useShareForm } from '../shared/useShareForm';
import CollapsableMessageForm from '../shared/CollapsableMessageForm';
import SendButtonPanel from '../shared/SendButtonPanel';
var DEBOUNCE_INTERVAL = 200;
function ShareEditor(_ref) {
  var data = _ref.data,
    defaultMessage = _ref.defaultMessage,
    defaultSubject = _ref.defaultSubject,
    onClose = _ref.onClose;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useShareContent = useShareContent(),
    shareContent = _useShareContent.shareContent;
  var type = data.type;
  var inputRef = useFocusOnMount();
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    formAlert = _useState2[0],
    setFormAlert = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    pendingSubmit = _useState4[0],
    setPendingSubmit = _useState4[1];
  var _useShareForm = useShareForm({
      defaultMessage: defaultMessage,
      defaultSubject: defaultSubject
    }),
    fields = _useShareForm.fields;
  var recipients = fields.recipients,
    message = fields.message,
    subject = fields.subject;
  function handleSubmit(_x) {
    return _handleSubmit.apply(this, arguments);
  }
  function _handleSubmit() {
    _handleSubmit = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(e) {
      var _getShareRefIds, share_ref_ids, share_context_ids, shareObj;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            e.preventDefault();
            if (!(recipients.value.length === 0)) {
              _context.next = 4;
              break;
            }
            setFormAlert(t('share_missing_recipients'));
            return _context.abrupt("return");
          case 4:
            // Mark the state as pending...
            setPendingSubmit(true);
            _getShareRefIds = getShareRefIds({
              type: type,
              playlistIds: data.playlistIds,
              assetIds: data.assetIds
            }), share_ref_ids = _getShareRefIds.share_ref_ids, share_context_ids = _getShareRefIds.share_context_ids;
            shareObj = {
              user_ids: recipients.value.filter(function (item) {
                return item.icon === 'user';
              }).map(function (item) {
                return item.id;
              }),
              team_ids: recipients.value.filter(function (item) {
                return item.icon === 'team';
              }).map(function (item) {
                return item.id;
              }),
              share_ref_ids: share_ref_ids,
              share_context_ids: share_context_ids,
              type: type,
              subject: subject.changedValue,
              message: message.changedValue
            };
            _context.prev = 7;
            _context.next = 10;
            return shareContent(shareObj);
          case 10:
            onClose();
            _context.next = 16;
            break;
          case 13:
            _context.prev = 13;
            _context.t0 = _context["catch"](7);
            if (_context.t0.status === 400) {
              setFormAlert(_context.t0.error);
            }
          case 16:
            // Allow input again...
            setPendingSubmit(false);
          case 17:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[7, 13]]);
    }));
    return _handleSubmit.apply(this, arguments);
  }
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    searchValue = _useState6[0],
    setSearchValue = _useState6[1];
  var _useDebounce = useDebounce(searchValue, DEBOUNCE_INTERVAL),
    _useDebounce2 = _slicedToArray(_useDebounce, 1),
    debouncedSearchValue = _useDebounce2[0];
  // Only use a debounced value if a search term exists
  // This prevents a laggy UI when clearing the search
  var searchQuery = searchValue ? debouncedSearchValue : '';
  var assetIds = type === 'asset' && data.assetIds ? data.assetIds : undefined;
  var playlistIds = type === 'playlist' && data.playlistIds ? data.playlistIds : undefined;

  // Fetch users and teams which can access the content
  var _useFetchContentUsers = useFetchContentUsersAndTeams({
      search: searchQuery,
      playlistIds: playlistIds,
      assetIds: assetIds,
      enabled: searchQuery.trim().length > 0
    }),
    usersAndTeams = _useFetchContentUsers.data,
    isLoading = _useFetchContentUsers.isLoading;
  var teams = (usersAndTeams === null || usersAndTeams === void 0 ? void 0 : usersAndTeams.teams.map(function (team) {
    return {
      value: team.name,
      icon: 'team',
      id: team.id
    };
  })) || [];
  var users = (usersAndTeams === null || usersAndTeams === void 0 ? void 0 : usersAndTeams.users.map(function (user) {
    return {
      title: user.first_name && user.last_name ? "".concat(user.first_name, " ").concat(user.last_name) : user.name,
      value: user.name,
      icon: 'user',
      id: user.id
    };
  })) || [];
  var teamList = teams.length ? [getOptionGroupLabel(t('content_type_team_plural'))].concat(_toConsumableArray(teams)) : null;
  var userList = users.length ? [getOptionGroupLabel(t('analytics_people'))].concat(_toConsumableArray(users)) : null;
  var recipientOptions = teamList && userList ? [].concat(_toConsumableArray(teamList), [getOptionDivider()], _toConsumableArray(userList)) : teamList || userList || [];
  function addRecipient(newRecipient) {
    setFormAlert('');
    setSearchValue('');
    recipients.set([].concat(_toConsumableArray(recipients.value), [newRecipient]));
  }
  function removeRecipient(item) {
    var updatedRecipients = recipients.value.filter(function (recipient) {
      return recipient.value.indexOf(item.value) === -1;
    });
    recipients.set(updatedRecipients);
  }
  return /*#__PURE__*/React.createElement(Panel, {
    as: "form",
    flush: true,
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(DefaultFormFieldLayout, {
    horizontal: false
  }, /*#__PURE__*/React.createElement(Panel, {
    flush: "top"
  }, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(FormField, {
    required: true,
    label: t('share_recipients'),
    description: t('share_recipients_instructions'),
    alert: formAlert
  }, /*#__PURE__*/React.createElement(MultiSelect, {
    ref: inputRef,
    inputValue: searchValue,
    selectedItems: recipients.value,
    options: recipientOptions,
    optionsLabel: t('share_recipients'),
    emptyMenuMessage: isLoading ? t('loading') : t('share_no_users_found'),
    onInputValueChange: setSearchValue,
    onSelectItem: addRecipient,
    onRemoveItem: removeRecipient
  })), /*#__PURE__*/React.createElement(CollapsableMessageForm, {
    subject: subject,
    message: message,
    handleSubmit: handleSubmit
  }))), /*#__PURE__*/React.createElement(SendButtonPanel, {
    isDisabled: pendingSubmit || !!message.error || !!subject.error
  })));
}
export default ShareEditor;